/// <amd-module name="VSS/Features/Charts/Internal/AxialChartImplementations" />
import * as Highcharts from "highcharts";
import * as Charts_Contracts from "VSS/Features/Charts/Contracts";
import { ChartTypesRegistry } from "VSS/Features/Charts/Internal/ChartTypesRegistry";
import * as Charts_Resources from "VSS/Features/Charts/Resources";
import { CommonChartImplementation } from "./CommonChartImplementation";
import { VssChartsSeriesOptions } from "./InternalContracts";

export abstract class AxialChartImplementation extends CommonChartImplementation {}

class LineChartImplementation extends AxialChartImplementation {}
ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.Line,
    constructor: LineChartImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_Line
});

class AreaChartImplementation extends AxialChartImplementation {
    public isOneDimensional(): boolean {
        return true;
    }

    /** The singular Area chart fill uses one color throughout, rather than separate colors by day. */
    public coloredBySeries(): boolean {
        return true;
    }
}
ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.Area,
    constructor: AreaChartImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_Area
});

class StackedAreaChartImplementation extends AxialChartImplementation {
    public mapSeriesType(chartType: string, index?: number): string {
        return Charts_Contracts.ChartTypesConstants.Area;
    }

    /**
     * Override the default z-ordering of stacked Area chart elements.
     * This ensures proper presentation of highcharts' SVG rendered focal dots, along with foreground elements.
     */
    public translateSeriesOptions(
        chartOptions: Charts_Contracts.ChartProps,
        seriesOptions: Charts_Contracts.DataSeries[],
        suppressAnimation: boolean,
        chartType: string,
        colorizer: any
    ): Highcharts.SeriesOptions[] {
        const convertedOption = super.translateSeriesOptions(chartOptions, seriesOptions, suppressAnimation, chartType, colorizer);

        if (convertedOption) {
            for (let i = 0; i < convertedOption.length; i++) {
                // Set reverse ordering of z-index for chart elements.
                convertedOption[i].zIndex = convertedOption.length - i;
            }
        }
        return convertedOption;
    }
}
ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.StackedArea,
    constructor: StackedAreaChartImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_StackedArea
});

class ScatterChartImplementation extends AxialChartImplementation {
    public translateSeriesOption(
        chartOptions: Charts_Contracts.ChartProps,
        seriesOption: Charts_Contracts.DataSeries,
        seriesIndex: number,
        suppressAnimation: boolean,
        chartType: string,
        colorizer: any
    ): Highcharts.SeriesOptions {
        const options: VssChartsSeriesOptions = super.translateSeriesOption(
            chartOptions,
            seriesOption,
            seriesIndex,
            suppressAnimation,
            chartType,
            colorizer
        );

        // Turn off sticky tracking for scatter plots to make it easier for users to hover between series points without
        // the tooltip getting in the way (since it won't fade until the mouseout event occurs on the tracked series).
        options.stickyTracking = false;

        return options;
    }
}
ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.Scatter,
    constructor: ScatterChartImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_Scatter
});

class StackedBarChartImplementation extends AxialChartImplementation {
    public mapSeriesType(chartType: string, index?: number): string {
        return Charts_Contracts.ChartTypesConstants.Bar;
    }
}

ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.StackedBar,
    constructor: StackedBarChartImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_StackedBar
});

class BarChartImplementation extends AxialChartImplementation {
    public isOneDimensional(): boolean {
        return true;
    }
}
ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.Bar,
    constructor: BarChartImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_Bar
});

class ColumnChartImplementation extends AxialChartImplementation {
    public isOneDimensional(): boolean {
        return true;
    }
}
ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.Column,
    constructor: ColumnChartImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_Column
});

class StackedColumnChartImplementation extends AxialChartImplementation {
    public mapSeriesType(chartType: string, index?: number): string {
        return Charts_Contracts.ChartTypesConstants.Column;
    }
}
ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.StackedColumn,
    constructor: StackedColumnChartImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_StackedColumn
});

class HistogramChartImplementation extends ColumnChartImplementation {
    public mapSeriesType(chartType: string, index?: number): string {
        return Charts_Contracts.ChartTypesConstants.Column;
    }

    /** The default histogram fill uses one color throughout, rather than separate colors by day. */
    public coloredBySeries(): boolean {
        return true;
    }
}
ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.Histogram,
    constructor: HistogramChartImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_Histogram
});

class ColumnLineChartImplementation extends AxialChartImplementation {
    public mapSeriesType(chartType: string, index?: number): string {
        return index == 0 ? Charts_Contracts.ChartTypesConstants.Column : Charts_Contracts.ChartTypesConstants.Line;
    }
}
ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.ColumnLine,
    constructor: ColumnLineChartImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_ColumnLine
});

/**
 * A flexible control intended for mixing *Trend* charts (area, line, column) together.
 * Merges together implementation of disparate chart behaviors at series level
 */
class HybridChartImplementation extends CommonChartImplementation {
    public translateOptions(chartOptions: Charts_Contracts.ChartProps): Highcharts.Options {
        const highchartsOptions = super.translateOptions(chartOptions);
        const chartTypes = (chartOptions.specializedOptions as Charts_Contracts.HybridChartOptions).chartTypes;

        if (highchartsOptions.series.length !== chartTypes.length) {
            throw new Error("Hybrid chart Types list does not match series list.");
        }

        chartTypes.forEach(chartType => {
            if (chartType === Charts_Contracts.ChartTypesConstants.Bubble) {
                throw new Error("Bubble chart cannot be combined with any other charts");
            }
        });

        return super.translateOptions(chartOptions);
    }

    public translateSeriesOption(
        chartOptions: Charts_Contracts.ChartProps,
        seriesOption: Charts_Contracts.DataSeries,
        seriesIndex: number,
        suppressAnimation: boolean,
        chartType: string,
        colorizer: any
    ): Highcharts.SeriesOptions {
        const chartTypes = this.getChartTypes();
        const seriesChartType = chartTypes[seriesIndex];
        const chartImplementation = ChartTypesRegistry.getInstance().instantiateType(seriesChartType);
        return chartImplementation.translateSeriesOption(chartOptions, seriesOption, seriesIndex, suppressAnimation, seriesChartType, colorizer);
    }

    private getChartTypes(): string[] {
        return (this._chartOptions.specializedOptions as Charts_Contracts.HybridChartOptions).chartTypes;
    }
}
ChartTypesRegistry.getInstance().registerChartType({
    chartTypeName: Charts_Contracts.ChartTypesConstants.Hybrid,
    constructor: HybridChartImplementation,
    userFacingChartTypeName: Charts_Resources.ChartType_A11yLabel_Hybrid
});
